import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../utils/api';

const WelcomeView = () => {
  const [rssFeedUrl, setRssFeedUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState('');
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const navigate = useNavigate();

  const handleAddFeed = async () => {
    if (!rssFeedUrl.trim()) {
      setError('Please enter an RSS feed URL');
      return;
    }

    try {
      setIsAdding(true);
      setError('');

      console.log('📝 Adding feed...');

      // Add the feed - userId is extracted from authenticated session by Lambda
      await apiPost('/addPodcastFeed', {
        rssFeedUrl: rssFeedUrl.trim()
      });

      console.log('✅ Feed added successfully');

      // Show processing modal
      setShowProcessingModal(true);
      setIsAdding(false);

      // Mark onboarding as complete in sessionStorage (for immediate session)
      sessionStorage.setItem('onboardingComplete', 'true');

      // Wait a moment to ensure feed is saved
      await new Promise(resolve => setTimeout(resolve, 500));

      // Dispatch event to tell App.js to refresh feeds check
      console.log('📡 Dispatching feeds-updated event');
      window.dispatchEvent(new Event('feeds-updated'));
      
    } catch (err) {
      console.error('❌ Error adding feed:', err);
      setError('Failed to add feed. Please check the URL and try again.');
      setIsAdding(false);
    }
  };

  const handleSkip = () => {
    // Mark onboarding as complete even if skipped
    sessionStorage.setItem('onboardingComplete', 'true');
    navigate('/dashboard', { replace: true });
  };

  const handleModalClose = () => {
    setShowProcessingModal(false);
    navigate('/dashboard', { replace: true });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isAdding) {
      handleAddFeed();
    }
  };

  return (
    <div className="welcome-view-container" id="welcomeViewContainer">
      <div className="welcome-card" id="welcomeCard">
        {/* Welcome Header */}
        <div className="welcome-header" id="welcomeHeader">
          <h1 className="welcome-title" id="welcomeTitle">
            Welcome to Smack! 🎉
          </h1>
          <p className="welcome-subtitle" id="welcomeSubtitle">
            Let's get you started by adding your first podcast feed
          </p>
        </div>

        {/* Value Proposition */}
        <div className="welcome-benefits" id="welcomeBenefits">
          <div className="welcome-benefit-item" id="benefitItem1">
            <div className="benefit-icon">📡</div>
            <div className="benefit-text">
              <h3>Automatic Processing</h3>
              <p>We'll automatically process new episodes as they're published</p>
            </div>
          </div>
          <div className="welcome-benefit-item" id="benefitItem2">
            <div className="benefit-icon">✨</div>
            <div className="benefit-text">
              <h3>Smart Summaries</h3>
              <p>Get AI-generated social media posts for every episode</p>
            </div>
          </div>
          <div className="welcome-benefit-item" id="benefitItem3">
            <div className="benefit-icon">🚀</div>
            <div className="benefit-text">
              <h3>Easy Sharing</h3>
              <p>Publish content everywhere with a single click</p>
            </div>
          </div>
        </div>

        {/* RSS Feed Input */}
        <div className="welcome-feed-section" id="welcomeFeedSection">
          <label className="welcome-feed-label" id="welcomeFeedLabel">
            Enter your podcast RSS feed URL
          </label>
          
          {error && (
            <div className="welcome-error-message" id="welcomeErrorMessage">
              {error}
            </div>
          )}

          <input
            type="text"
            placeholder="https://example.com/feed.xml"
            value={rssFeedUrl}
            onChange={(e) => setRssFeedUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            className="welcome-feed-input"
            id="welcomeFeedInput"
            disabled={isAdding}
          />

          <p className="welcome-feed-help" id="welcomeFeedHelp">
            Don't have a feed URL handy? You can always add it later from your dashboard.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="welcome-actions" id="welcomeActions">
          <button
            onClick={handleAddFeed}
            disabled={isAdding}
            className={`welcome-btn welcome-btn-primary ${isAdding ? 'welcome-btn-loading' : ''}`}
            id="welcomeAddFeedBtn"
          >
            {isAdding ? 'Adding Feed...' : 'Add Feed & Continue'}
          </button>
          
          <button
            onClick={handleSkip}
            disabled={isAdding}
            className="welcome-btn welcome-btn-secondary"
            id="welcomeSkipBtn"
          >
            Skip for Now
          </button>
        </div>
      </div>

      {/* Processing Modal */}
      {showProcessingModal && (
        <div className="feed-processing-modal-overlay">
          <div className="feed-processing-modal">
            <div className="feed-processing-modal-icon">🎉</div>
            <h2 className="feed-processing-modal-title">Feed Processing Started!</h2>
            <p className="feed-processing-modal-message">
              Your podcast feed has been added and is now being processed. We'll notify you when your episodes are ready to view.
            </p>
            <div className="feed-processing-modal-spinner"></div>
            <button
              onClick={handleModalClose}
              className="feed-processing-modal-close"
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WelcomeView;